/* ============================================================
   <pdp-option-cards> — standalone section.
   Identical card visuals to main-product-section's option_cards_full
   block (reuses the same snippet + CSS classes). Only behavior here:
     • Radio-card switching for the two option cards (mirrors
       _initFullOptionCards from main-product-section, scoped to this
       host so it doesn't conflict with the block's own cards).
     • Relocating the ATC block so it always sits under the active card
       (its initial position is rendered server-side by the section).
     • ATC stays on Alpine x-data="xProductCart" — no fetch logic.
   ============================================================ */

class PdpOptionCards extends HTMLElement {
  connectedCallback() {
    this._bindCards();
    this._syncInitial();
  }

  _bindCards() {
    const cards = this.querySelectorAll('[data-full-card]');
    if (cards.length === 0) return;

    cards.forEach((card) => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('[data-mps-ipd-trigger]')) return; // included-item click belongs to the drawer
        this._selectCard(card);
      });
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          if (e.target.closest('[data-mps-ipd-trigger]')) return;
          e.preventDefault();
          this._selectCard(card);
        }
      });
    });
  }

  _selectCard(card) {
    this.querySelectorAll('[data-full-card]').forEach((c) => {
      c.classList.remove('section-mps__full-card--active');
      c.setAttribute('aria-checked', 'false');
    });
    card.classList.add('section-mps__full-card--active');
    card.setAttribute('aria-checked', 'true');
    this._moveCtaAfter(card);
    this._syncSelection(card);
  }

  /* Keep the ATC directly beneath the selected card. Alpine 3.10+ skips
     destroy/init for nodes that were only relocated, so the x-data on the
     CTA survives the move with its state intact. */
  _moveCtaAfter(card) {
    const cta = this.querySelector('[data-full-cta]');
    if (!cta || cta.previousElementSibling === card) return;
    card.after(cta);
  }

  /* Push variant id + price from the active card into the hidden form
     input + ATC label. Matches main-product-section's _syncFullSelection. */
  _syncSelection(card) {
    if (!card) return;
    const variantId = card.dataset.variantId;

    // Show the discounted price when the card has one, else the sale price.
    const discountedEl = card.querySelector('[data-discounted-price]');
    const saleEl = card.querySelector('.section-mps__full-card-sale');
    const priceFormatted =
      (discountedEl && discountedEl.textContent.trim()) ||
      (saleEl && saleEl.textContent.trim()) ||
      card.dataset.priceFormatted ||
      '';

    if (variantId) {
      const fullInput = this.querySelector('[data-full-variant-input]');
      if (fullInput) fullInput.value = variantId;
    }

    const priceEl = this.querySelector('[data-full-atc-price]');
    if (priceEl && priceFormatted) priceEl.textContent = priceFormatted;
  }

  _syncInitial() {
    const active = this.querySelector('[data-full-card].section-mps__full-card--active');
    if (active) this._syncSelection(active);
  }
}

if (!customElements.get('pdp-option-cards')) {
  customElements.define('pdp-option-cards', PdpOptionCards);
}
